import {
  doc,
  getDoc,
  getDocs,
  collection,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  serverTimestamp,
} from 'firebase/firestore';
import { firebaseFirestore } from '@/infrastructure/firebase/firebaseFirestore.js';
import { type SimulationTest } from '@k-ucap/shared';
import { getCurrentEnvironment, buildConverter } from './firestoreUtils.js';
import type { SimulationTestRepository } from '@/infrastructure/repositories/RepositoryInterfaces.js';

const SIMULATION_TESTS = 'simulationTests';

const converter = buildConverter<SimulationTest>(
  (data) => data as SimulationTest,
  (model) => ({ ...model, createdAt: serverTimestamp(), updatedAt: serverTimestamp() })
);

export class FirestoreSimulationTestRepository implements SimulationTestRepository {
  async findAll(): Promise<SimulationTest[]> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    const q = query(
      collection(db, SIMULATION_TESTS),
      where('environment', '==', getCurrentEnvironment())
    ).withConverter(converter);
    const snap = await getDocs(q);
    return snap.docs.map((d) => d.data());
  }

  async findById(id: string): Promise<SimulationTest | undefined> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    const snap = await getDoc(doc(db, SIMULATION_TESTS, id).withConverter(converter));
    return snap.data();
  }

  async findByProjectId(projectId: string): Promise<SimulationTest[]> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    const q = query(
      collection(db, SIMULATION_TESTS),
      where('projectId', '==', projectId),
      where('environment', '==', getCurrentEnvironment())
    ).withConverter(converter);
    const snap = await getDocs(q);
    return snap.docs.map((d) => d.data());
  }

  async save(test: SimulationTest): Promise<SimulationTest> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    const existing = await this.findById(test.id);
    const data = { ...test, environment: getCurrentEnvironment() };
    if (existing) {
      await updateDoc(doc(db, SIMULATION_TESTS, test.id), { ...data, updatedAt: serverTimestamp() });
    } else {
      await setDoc(doc(db, SIMULATION_TESTS, test.id).withConverter(converter), data);
    }
    return { ...data, createdAt: existing?.createdAt ?? new Date().toISOString(), updatedAt: new Date().toISOString() };
  }

  async delete(id: string): Promise<void> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    await deleteDoc(doc(db, SIMULATION_TESTS, id));
  }

  async duplicate(id: string, newName?: string): Promise<SimulationTest | undefined> {
    const original = await this.findById(id);
    if (!original) return undefined;
    const duplicated: SimulationTest = {
      ...original,
      id: `${original.id}-copy-${Date.now()}`,
      name: newName || `${original.name} (Copy)`,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    await this.save(duplicated);
    return duplicated;
  }
}

export const createFirestoreSimulationTestRepository = (): FirestoreSimulationTestRepository =>
  new FirestoreSimulationTestRepository();
